import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHead from '@/components/feature/SeoHead';
import { categories } from '@/mocks/home';
import { allGames } from '@/mocks/games';
import { allArticles, articleCategories } from '@/mocks/articles';

type TabType = 'games' | 'articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState<TabType>(tabParam === 'articles' ? 'articles' : 'games');
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCategory, activeTab]);

  useEffect(() => {
    if (slug !== undefined) {
      setActiveCategory(slug);
      setActiveTab(tabParam === 'articles' ? 'articles' : 'games');
    }
  }, [slug, tabParam]);

  const filteredGames = useMemo(() => {
    if (activeCategory === 'all') return allGames;
    return allGames.filter((g) => g.category === activeCategory);
  }, [activeCategory]);

  const filteredArticles = useMemo(() => {
    return allArticles;
  }, []);

  const categoryTabs = [
    { slug: 'all', name: '全部游戏', icon: 'ri-apps-line' },
    ...categories.map((c) => ({ slug: c.slug, name: c.name, icon: c.icon })),
  ];

  const currentCategoryName = categoryTabs.find((c) => c.slug === activeCategory)?.name || '全部游戏';

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    setActiveTab('games');
    navigate(`/category/${newSlug}`, { replace: true });
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === 'articles') {
      navigate(`/category/${activeCategory}?tab=articles`, { replace: true });
    } else {
      navigate(`/category/${activeCategory}`, { replace: true });
    }
  };

  return (
    <main>
      <SeoHead
        title={`${currentCategoryName} - 游戏大厅 | 九游娱乐`}
        description={`九游娱乐${currentCategoryName}板块，提供丰富的${currentCategoryName}游戏选择，高品质游戏体验，安全稳定的游戏环境。`}
        keywords={`九游娱乐,${currentCategoryName},游戏大厅,在线游戏`}
        canonicalUrl={`https://www.jiuyou.com/category/${activeCategory}`}
        schemaType="WebPage"
      />
      <Navbar />

      <div className="pt-16 md:pt-18">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-800 font-medium">游戏大厅</span>
              {activeCategory !== 'all' && (
                <>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                  <span className="text-foreground-800 font-medium">{currentCategoryName}</span>
                </>
              )}
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">游戏大厅</h1>
            <p className="text-foreground-600 text-sm">探索九游娱乐海量精品游戏，总有一款适合你</p>
          </div>
        </div>

        <div className="w-full bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4 pt-4">
              <button
                onClick={() => handleTabChange('games')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'games'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                }`}
              >
                <i className="ri-gamepad-line mr-1.5"></i>游戏
              </button>
              <button
                onClick={() => handleTabChange('articles')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === 'articles'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                }`}
              >
                <i className="ri-article-line mr-1.5"></i>资讯
              </button>
            </div>

            {activeTab === 'games' && (
              <div className="flex items-center gap-1.5 pb-4 overflow-x-auto flex-wrap">
                {categoryTabs.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-12 bg-background-50 min-h-[400px]">
          {activeTab === 'games' ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                  {currentCategoryName} <span className="text-foreground-500 font-normal text-sm">({filteredGames.length}款游戏)</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {filteredGames.map((game) => (
                  <div
                    key={game.id}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <div className="relative h-40 md:h-44 overflow-hidden">
                      <img
                        src={game.image}
                        alt={game.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      {game.label && (
                        <div className="absolute top-2.5 left-2.5">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            game.label === '热门'
                              ? 'bg-primary-500 text-background-50'
                              : game.label === '推荐'
                                ? 'bg-accent-500 text-background-50'
                                : 'bg-secondary-500 text-background-50'
                          }`}>
                            {game.label}
                          </span>
                        </div>
                      )}
                      <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-background-50/90 backdrop-blur-sm rounded-full px-2 py-0.5">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-foreground-800 text-xs font-medium">{game.rating}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1.5">{game.name}</h3>
                      <p className="text-foreground-600 text-xs mb-3">{game.desc}</p>
                      <div className="flex items-center text-foreground-500 text-xs">
                        <i className="ri-user-line mr-1"></i>
                        <span>{(game.players / 10000).toFixed(1)}万人在玩</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredGames.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-gamepad-line text-foreground-400 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无游戏</p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                  最新资讯 <span className="text-foreground-500 font-normal text-sm">({filteredArticles.length}篇)</span>
                </h2>
              </div>

              <div className="flex items-center gap-2 mb-6 flex-wrap">
                {articleCategories.map((cat) => (
                  <button
                    key={cat}
                    className="px-4 py-2 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2.5 left-2.5">
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-foreground-500 text-xs">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article.date}</span>
                          <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{(article.views / 10000).toFixed(1)}万</span>
                        </div>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
      <ScrollToTop />
    </main>
  );
}